/**
 * Every mutation the assessment editor can make. Two invariants hold throughout, and both are
 * load-bearing:
 *
 *  1. MUTATE IN PLACE, NEVER REBUILD. The working document may carry keys no schema describes (B9),
 *     and `PATCH` is a full replacement, so rebuilding a node from its known fields would delete
 *     the unknown ones permanently. Splices and property assignment only.
 *  2. REORDER EDGES ARE NO-OPS. One helper (`moveWithin`) enforces it for every collection, so there
 *     is no per-collection edge case to get wrong.
 *
 * Addressing is explicit: every mutation names its target, and nothing reads the current selection,
 * so a component can edit a node it is not "on".
 */
import type { Activity, AssessmentTemplate, AssessmentTemplatePayload, Choice, ScoredRubricLevel, Screen } from '../interfaces'
import type { ContentBlock, Interaction, InteractionType, ReviewRubric, RubricCatalog } from '../types'
import type { ActivityPatch, AssessmentEditorSnapshot, AssessmentEditorStore, AssessmentWorkingDocument, AudioBlockPatch, ChoicePatch, ContentBlockOwner, ContentBlockType, DragTheWordsPatch, FillInBlanksPatch, ImageBlockPatch, MultipleChoicePatch, OpenEndedPatch, RubricLevelPatch, RubricPatch, ScreenPatch, TextBlockPatch, WhiteboardPatch } from './interfaces'

import { ID_PREFIX, mintId } from '@shared/lib/collections'

import { DEFAULT_OPEN_ENDED_FORMATS, RUBRIC_LEVEL_SCORES } from '../../../lib/assessments/schema'
import { isAssessmentMediaPath } from '../../../lib/assessments/assessment-media'
import { toSavePayload } from './getters'

// --- Private helpers -------------------------------------------------------

/** The document is JSON by definition — it lives in a `json` column — so a JSON round trip is a
 *  faithful deep copy. Used for the pristine snapshot and the interaction stash, so neither aliases
 *  the live document. */
const cloneJson = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

/**
 * Applies one optional field of a patch: `undefined` leaves it alone, `null` deletes the key,
 * anything else assigns. Keys are deleted rather than set to `undefined`, so the document never
 * serialises an explicit `null` the schema does not allow.
 */
const patchOptional = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | null | undefined,
): void => {
  if (value === undefined) {
    return
  }
  if (value === null) {
    Reflect.deleteProperty(target, key)
    return
  }
  target[key] = value
}

/** Assigns a required field only when the patch mentions it. */
const patchRequired = <T extends object, K extends keyof T>(
  target: T,
  key: K,
  value: T[K] | undefined,
): void => {
  if (value === undefined) {
    return
  }
  target[key] = value
}

/**
 * Moves `list[index]` by `delta`. THE SINGLE PLACE REORDER EDGES ARE DECIDED: an out-of-range
 * source or destination is a no-op, so `moveUp` on the first element and `moveDown` on the last
 * leave the array byte-identical.
 */
const moveWithin = <T>(list: T[], index: number, delta: number): void => {
  const target = index + delta
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) {
    return
  }
  const [item] = list.splice(index, 1)
  if (item === undefined) {
    return
  }
  list.splice(target, 0, item)
}

const screensOf = (state: AssessmentEditorStore): Screen[] =>
  state.workingDocument?.definition.screens ?? []

const findScreen = (state: AssessmentEditorStore, screenId: string): Screen | null =>
  screensOf(state).find(screen => screen.id === screenId) ?? null

const findActivity = (
  state: AssessmentEditorStore,
  screenId: string,
  activityId: string,
): Activity | null =>
  findScreen(state, screenId)?.activities.find(activity => activity.id === activityId) ?? null

/** The `ContentBlock[]` an owner names — a screen's own content, or one of its activities'. */
const findContent = (
  state: AssessmentEditorStore,
  owner: ContentBlockOwner,
): ContentBlock[] | null => {
  const screen = findScreen(state, owner.screenId)
  if (!screen) {
    return null
  }
  if (!owner.activityId) {
    return screen.content
  }
  return screen.activities.find(activity => activity.id === owner.activityId)?.content ?? null
}

/** The activity's interaction, narrowed to one member of the union — `null` when the activity has
 *  none or a different one, which makes every per-type patch a safe no-op. */
const findInteraction = <T extends InteractionType>(
  state: AssessmentEditorStore,
  screenId: string,
  activityId: string,
  type: T,
): Extract<Interaction, { type: T }> | null => {
  const interaction = findActivity(state, screenId, activityId)?.interaction
  if (!interaction || interaction.type !== type) {
    return null
  }
  return interaction as Extract<Interaction, { type: T }>
}

/** The catalog, created on first use. `rubrics` is optional on the document, and an absent catalog
 *  is treated exactly like an empty one. */
const ensureCatalog = (state: AssessmentEditorStore): RubricCatalog | null => {
  if (!state.workingDocument) {
    return null
  }
  if (!state.workingDocument.rubrics) {
    state.workingDocument.rubrics = {}
  }
  return state.workingDocument.rubrics
}

const findRubric = (state: AssessmentEditorStore, rubricId: string): ReviewRubric | null =>
  state.workingDocument?.rubrics?.[rubricId] ?? null

/**
 * NOT a kind check — there is one rubric type (B10). This is a SHAPE guard, and it exists because
 * the working document is raw (B9): the store never rebuilds it, so it can hold a rubric this editor
 * cannot author — an observation rubric written before B10 has `prompts` and no `levels`. Validation
 * rejects such a rubric on save; until then the level actions must be no-ops rather than
 * `TypeError`s. It is not a repair or a conversion: nothing here writes a `levels` array onto a
 * rubric that arrived without one.
 */
const hasLevels = (rubric: ReviewRubric): boolean => Array.isArray(rubric.levels)

/**
 * Deletes the catalog entries of activities that just left the document. A rubric is edited only
 * inside the question that references it, so once that question is gone its entry is unreachable
 * from the editor yet still validated on every save — an error the author has no way to open.
 * Call AFTER the splice: an id some remaining activity still references is kept.
 */
const dropRubricsOf = (state: AssessmentEditorStore, removed: Activity[]): void => {
  const catalog = state.workingDocument?.rubrics
  if (!catalog) {
    return
  }
  const referenced = new Set(
    screensOf(state).flatMap(screen => screen.activities.flatMap(activity => activity.rubrics ?? [])),
  )
  removed.forEach((activity) => {
    activity.rubrics?.forEach((rubricId) => {
      if (!referenced.has(rubricId)) {
        Reflect.deleteProperty(catalog, rubricId)
      }
    })
  })
}

// --- media manifest (B13.11 · media contract §1, §3, §4) -------------------
//
// The traversal B13.9 refused to put in PHP, and it belongs here for the reason that decision
// gives: the backend stores `data` verbatim and never validates it, while this side owns the
// document, knows the moment an upload happens, and Zod-checks the result.
//
// EVERY READ IS GUARDED, in the style of `preview-content.hook`. The working document is
// half-authored by definition, and reconciliation runs on every save, so a `TypeError` here would
// be a save the author cannot complete.

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Keeps `value` only when it is an object path (§3). An external URL, an empty string, a missing
 *  key or a non-string is skipped in silence — none of them needs signing. */
const collectMediaPath = (value: unknown, into: Set<string>): void => {
  if (typeof value !== 'string') {
    return
  }
  const trimmed = value.trim()
  if (isAssessmentMediaPath(trimmed)) {
    into.add(trimmed)
  }
}

/**
 * Three of the four field kinds in §4 — `ImageBlock.asset.url`, `AudioBlock.asset.url` and
 * `TextBlock.audioUrl` — over one `ContentBlock[]`, which is a screen's own content or an
 * activity's.
 *
 * Read by SHAPE, not by `block.type`: the discriminant is authored data too, and a mistyped or
 * absent one must not be able to hide a path from the manifest. Missing one is a broken image for
 * every kid.
 */
const collectContentMediaPaths = (content: unknown, into: Set<string>): void => {
  if (!Array.isArray(content)) {
    return
  }
  content.forEach((block: unknown) => {
    if (!isRecord(block)) {
      return
    }
    if (isRecord(block.asset)) {
      collectMediaPath(block.asset.url, into)
    }
    collectMediaPath(block.audioUrl, into)
  })
}

/** The fourth field kind: `Whiteboard.backgroundAsset.url`, which lives on the interaction rather
 *  than in a content block. */
const collectInteractionMediaPaths = (interaction: unknown, into: Set<string>): void => {
  if (!isRecord(interaction) || !isRecord(interaction.backgroundAsset)) {
    return
  }
  collectMediaPath(interaction.backgroundAsset.url, into)
}

/** Every object path the document actually references, deduplicated. Order follows the document and
 *  is not meaningful (§1). */
const collectDocumentMediaPaths = (document: AssessmentWorkingDocument | null): string[] => {
  const found = new Set<string>()
  const definition: unknown = document?.definition
  const screens: unknown = isRecord(definition) ? definition.screens : null

  if (!Array.isArray(screens)) {
    return []
  }

  screens.forEach((screen: unknown) => {
    if (!isRecord(screen)) {
      return
    }
    collectContentMediaPaths(screen.content, found)

    if (!Array.isArray(screen.activities)) {
      return
    }
    screen.activities.forEach((activity: unknown) => {
      if (!isRecord(activity)) {
        return
      }
      collectContentMediaPaths(activity.content, found)
      collectInteractionMediaPaths(activity.interaction, found)
    })
  })

  return [...found]
}

/**
 * Replaces the manifest with what the document references — the half of B13.11 that makes drift
 * impossible in both directions at once: a path no block references any more is dropped, and a path
 * a block gained without an upload (paste, duplicate, undo) is added.
 *
 * A document with no media keeps NO `media` key rather than gaining an empty array: absent and empty
 * mean the same thing (§1), and writing `[]` would report a document nobody edited as dirty (B7).
 */
const reconcileMediaManifest = (state: AssessmentEditorStore): void => {
  if (!state.workingDocument) {
    return
  }
  const paths = collectDocumentMediaPaths(state.workingDocument)

  if (paths.length === 0 && !state.workingDocument.media) {
    return
  }
  state.workingDocument.media = paths
}

// ---- factories: what a freshly added node looks like ----

const createScreen = (): Screen => ({
  id: mintId(ID_PREFIX.screen),
  layout: 'single',
  content: [],
  activities: [],
})

/** No `interaction`: a brand-new activity is a passive prompt until a type is chosen, which is a
 *  legitimate authored state. */
const createActivity = (): Activity => ({
  id: mintId(ID_PREFIX.activity),
  content: [],
})

const createChoice = (): Choice => ({
  id: mintId(ID_PREFIX.choice),
  text: '',
  correct: false,
})

const createContentBlock = (type: ContentBlockType): ContentBlock => {
  if (type === 'image') {
    return { type: 'image', asset: { url: '' }, alt: '' }
  }
  if (type === 'audio') {
    return { type: 'audio', asset: { url: '' } }
  }
  return { type: 'text', value: '' }
}

/** Minimum viable shapes: `multiple_choice` needs 2 options, so it is born with exactly that rather
 *  than an array the schema already rejects.
 *
 *  `fill_in_blanks` is born EMPTY — a blank has no meaning apart from a gap in the text, and a
 *  pre-seeded one would be an answer key for a gap that does not exist. The empty state is invalid
 *  on save (refinement 2), the correct report for a sentence nobody has written yet. */
const createInteraction = (type: InteractionType): Interaction => {
  switch (type) {
    case 'multiple_choice':
      return { type: 'multiple_choice', mode: 'single', options: [createChoice(), createChoice()] }
    case 'fill_in_blanks':
      return { type: 'fill_in_blanks', text: '', blanks: [] }
    case 'drag_the_words':
      return { type: 'drag_the_words', text: '' }
    case 'whiteboard':
      return { type: 'whiteboard' }
    case 'audio_response':
      return { type: 'audio_response' }
    default:
      return { type: 'open_ended', formats: [...DEFAULT_OPEN_ENDED_FORMATS] }
  }
}

/** `kind: 'scored'` is written even though it is optional here (B8): it is lib's discriminant, and
 *  app-student/app-tutor still read the union, so a document this editor writes stays
 *  self-describing to them. */
const createRubric = (): ReviewRubric => ({
  id: mintId(ID_PREFIX.rubric),
  kind: 'scored',
  name: '',
  // The three fixed bands (`RUBRIC_LEVEL_SCORES`), born with the rubric: the levels list is static,
  // so a rubric that started empty would be one the editor could never complete.
  levels: RUBRIC_LEVEL_SCORES.map(score => ({ score })),
})

// ---- selection maintenance ----

/** After a removal at `removedIndex`, the neighbour that takes its place: the one that shifted up
 *  into the slot, or the previous one when the last item was removed. */
const neighbourId = (list: { id: string }[], removedIndex: number): string | null =>
  list[removedIndex]?.id ?? list[removedIndex - 1]?.id ?? null

/** Drops a selection that points at a node the document no longer contains. */
const pruneSelection = (state: AssessmentEditorStore): void => {
  const screen = state.selectedScreenId
    ? screensOf(state).find(item => item.id === state.selectedScreenId) ?? null
    : null
  if (!screen) {
    state.selectedScreenId = screensOf(state)[0]?.id ?? null
    state.selectedActivityId = null
    return
  }
  if (
    state.selectedActivityId
    && !screen.activities.some(activity => activity.id === state.selectedActivityId)
  ) {
    state.selectedActivityId = null
  }
}

const takeSnapshot = (state: AssessmentEditorStore): AssessmentEditorSnapshot | null => {
  if (!state.workingDocument) {
    return null
  }
  return {
    title: state.title,
    type: state.type,
    disciplineCode: state.disciplineCode,
    grade: state.grade,
    document: cloneJson(state.workingDocument),
  }
}

/**
 * The one place a `template` becomes editor state. The document is cloned on the way in so the
 * editor can never mutate the object a caller still holds — but cloned WHOLE, so unknown keys come
 * along (B9). It is not parsed, normalised or cleaned here: the transport layer already applied the
 * one permitted transform (B7's empty-map repair).
 */
const applyTemplate = (
  state: AssessmentEditorStore,
  template: AssessmentTemplate,
  keepSelection: boolean,
): void => {
  state.templateId = template.id
  state.title = template.title
  state.type = template.type
  state.disciplineCode = template.discipline_code
  state.grade = template.grade
  state.status = template.status
  state.workingDocument = cloneJson(template.data) as AssessmentWorkingDocument
  state.pristine = takeSnapshot(state)
  if (!keepSelection) {
    state.selectedScreenId = null
    state.selectedActivityId = null
    state.interactionDrafts = {}
  }
  pruneSelection(state)
}

// --- Actions ---------------------------------------------------------------

export const actions = {
  // ---- lifecycle -----------------------------------------------------------

  /** Loads a template fetched by T07's `show` hook. Selection starts on the first screen. */
  loadTemplate (this: AssessmentEditorStore, payload: AssessmentTemplate): void {
    applyTemplate(this, payload, false)
  },

  /**
   * Re-seats the editor on the response of a save. Same as `loadTemplate` except the selection and
   * the interaction drafts survive — losing your place on every Save would be its own bug.
   * Re-snapshotting is what clears `isDirty`.
   */
  applySavedTemplate (this: AssessmentEditorStore, payload: AssessmentTemplate): void {
    applyTemplate(this, payload, true)
  },

  /** Back to pristine — the document, the row fields, and a selection pruned to what survived. */
  reset (this: AssessmentEditorStore): void {
    if (!this.pristine) {
      return
    }
    this.title = this.pristine.title
    this.type = this.pristine.type
    this.disciplineCode = this.pristine.disciplineCode
    this.grade = this.pristine.grade
    this.workingDocument = cloneJson(this.pristine.document)
    this.interactionDrafts = {}
    pruneSelection(this)
  },

  /** Leaving the editor. Everything goes, so the next template cannot inherit this one's state. */
  clear (this: AssessmentEditorStore): void {
    this.templateId = null
    this.title = ''
    this.type = null
    this.disciplineCode = null
    this.grade = null
    this.status = null
    this.workingDocument = null
    this.pristine = null
    this.selectedScreenId = null
    this.selectedActivityId = null
    this.interactionDrafts = {}
    this.loading = false
    this.saving = false
  },

  setLoading (this: AssessmentEditorStore, payload: AssessmentEditorStore['loading']): void {
    this.loading = payload
  },

  setSaving (this: AssessmentEditorStore, payload: AssessmentEditorStore['saving']): void {
    this.saving = payload
  },

  // --- media manifest (B13.11) ----------------------------------------------
  //
  // APPENDED ON UPLOAD, RECONCILED ON SAVE — both, deliberately. Append alone drifts in two
  // directions: a stale entry bloats every response for every kid, and a missing one renders a
  // broken image with nothing to detect it. Reconciliation makes both impossible, so `addMediaPath`
  // is only the fast path that keeps the manifest honest between saves.

  /**
   * Records the object just uploaded. Called from the uploader's success path, and only there —
   * nothing else knows a path is new. Ignores a duplicate (the same asset may legitimately be
   * referenced twice) and anything that is not an object path, which is the guard that keeps a
   * signed URL out of the document (B13.8, the failure mode this design exists to prevent).
   */
  addMediaPath (this: AssessmentEditorStore, path: string): void {
    if (!this.workingDocument) {
      return
    }
    const trimmed = typeof path === 'string' ? path.trim() : ''

    if (!isAssessmentMediaPath(trimmed)) {
      return
    }
    this.workingDocument.media ??= []

    if (!this.workingDocument.media.includes(trimmed)) {
      this.workingDocument.media.push(trimmed)
    }
  },

  /**
   * Re-derives the manifest from the document. Exposed for tests and any future save path; the
   * editor's own save goes through `buildSavePayload`, which calls it for you.
   *
   * NOT a removal hook: removing or replacing an image deliberately does nothing to the manifest
   * (the same path can be referenced twice), so this is what prunes, once, when the document is
   * final.
   */
  reconcileMedia (this: AssessmentEditorStore): void {
    reconcileMediaManifest(this)
  },

  /**
   * The save body, with a reconciled manifest guaranteed.
   *
   * An action and not the `getPayload` getter because a getter cannot mutate: reconciliation would
   * become a separate call the save path must remember to make first, and a manifest only some save
   * paths refresh is exactly the drift B13.11 removes. Binding the two together means no save can
   * bypass reconciliation without deliberately assembling its own body.
   */
  buildSavePayload (this: AssessmentEditorStore): AssessmentTemplatePayload | null {
    reconcileMediaManifest(this)

    return toSavePayload(this)
  },

  // ---- row-level fields ----------------------------------------------------

  /**
   * The ROW title only — the catalogue handle a tutor picks from ("Baseline Math - Grade 4").
   *
   * It used to mirror into `definition.title` on every keystroke; the two are now meant to differ,
   * since the kid is shown the story's own name ("The Astronaut's Dilemma"), which
   * `setDefinitionTitle` edits. Creation still seeds them identically, so a template nobody
   * retitles behaves exactly as before.
   */
  setTitle (this: AssessmentEditorStore, payload: AssessmentEditorStore['title']): void {
    this.title = payload
  },

  // ---- definition-level fields ---------------------------------------------

  /** The kid-facing name, on the intro screen. Blank is not deletable — the schema requires a title
   *  — so an emptied field writes the empty string and validation asks for one. */
  setDefinitionTitle (this: AssessmentEditorStore, payload: string): void {
    if (this.workingDocument) {
      this.workingDocument.definition.title = payload
    }
  },

  /** The hook under the title. Emptied ⇒ the key goes, and the player falls back to its own line
   *  rather than showing a blank space where the hook was. */
  setDefinitionDescription (this: AssessmentEditorStore, payload: string): void {
    if (this.workingDocument) {
      patchOptional(this.workingDocument.definition, 'description', payload.trim() || null)
    }
  },

  setType (this: AssessmentEditorStore, payload: AssessmentEditorStore['type']): void {
    this.type = payload
  },

  /**
   * Mirrored into the definition for the same reason as `setTitle` — but only when there is a value
   * to mirror, and that asymmetry is forced by the types rather than an oversight.
   *
   * `title` is a `string` at both levels, so it always has something to write. `disciplineCode` and
   * `grade` are nullable HERE ONLY (the row-level "no template loaded" state), while the definition's
   * are `z.enum`, so clearing them would write `null` into a field the schema rejects. The stale
   * definition value is the lesser evil and is unreachable in practice: `getPayload` returns `null`
   * while either field is `null`, so such a document can never be saved.
   */
  setDisciplineCode (
    this: AssessmentEditorStore,
    payload: AssessmentEditorStore['disciplineCode'],
  ): void {
    this.disciplineCode = payload
    if (this.workingDocument && payload) {
      this.workingDocument.definition.disciplineCode = payload
    }
  },

  /** Mirrored into the definition when set; see `setDisciplineCode` for why clearing does not. */
  setGrade (this: AssessmentEditorStore, payload: AssessmentEditorStore['grade']): void {
    this.grade = payload
    if (this.workingDocument && payload) {
      this.workingDocument.definition.grade = payload
    }
  },

  // ---- selection -----------------------------------------------------------

  /** Selecting a different screen clears the activity selection — an activity id is only meaningful
   *  inside the screen that holds it. */
  selectScreen (this: AssessmentEditorStore, screenId: string | null): void {
    if (this.selectedScreenId === screenId) {
      return
    }
    this.selectedScreenId = screenId
    this.selectedActivityId = null
  },

  selectActivity (this: AssessmentEditorStore, activityId: string | null): void {
    this.selectedActivityId = activityId
  },

  // ---- screens -------------------------------------------------------------

  /** Appends an empty screen, selects it, and returns its id. */
  addScreen (this: AssessmentEditorStore): string | null {
    if (!this.workingDocument) {
      return null
    }
    const screen = createScreen()
    this.workingDocument.definition.screens.push(screen)
    this.selectedScreenId = screen.id
    this.selectedActivityId = null
    return screen.id
  },

  removeScreen (this: AssessmentEditorStore, screenId: string): void {
    const screens = screensOf(this)
    const index = screens.findIndex(screen => screen.id === screenId)
    if (index < 0) {
      return
    }
    const [removed] = screens.splice(index, 1)
    removed?.activities.forEach((activity) => {
      Reflect.deleteProperty(this.interactionDrafts, activity.id)
    })
    dropRubricsOf(this, removed?.activities ?? [])
    if (this.selectedScreenId === screenId) {
      this.selectedScreenId = neighbourId(screens, index)
      this.selectedActivityId = null
    }
  },

  moveScreenUp (this: AssessmentEditorStore, screenId: string): void {
    const screens = screensOf(this)
    moveWithin(screens, screens.findIndex(screen => screen.id === screenId), -1)
  },

  moveScreenDown (this: AssessmentEditorStore, screenId: string): void {
    const screens = screensOf(this)
    moveWithin(screens, screens.findIndex(screen => screen.id === screenId), 1)
  },

  /** `cta: null` and `isLast: false` delete their keys — absent is the schema's default (the player
   *  picks its own wording; the final screen submits). */
  updateScreen (this: AssessmentEditorStore, screenId: string, patch: ScreenPatch): void {
    const screen = findScreen(this, screenId)
    if (!screen) {
      return
    }
    patchRequired(screen, 'layout', patch.layout)
    patchOptional(screen, 'cta', patch.cta)
    patchOptional(screen, 'isLast', patch.isLast === false ? null : patch.isLast)
  },

  // ---- content blocks (screen content OR activity content) ------------------

  addContentBlock (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    type: ContentBlockType,
  ): void {
    findContent(this, owner)?.push(createContentBlock(type))
  },

  removeContentBlock (this: AssessmentEditorStore, owner: ContentBlockOwner, index: number): void {
    const content = findContent(this, owner)
    if (!content || index < 0 || index >= content.length) {
      return
    }
    content.splice(index, 1)
  },

  moveContentBlockUp (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    index: number,
  ): void {
    const content = findContent(this, owner)
    if (content) {
      moveWithin(content, index, -1)
    }
  },

  moveContentBlockDown (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    index: number,
  ): void {
    const content = findContent(this, owner)
    if (content) {
      moveWithin(content, index, 1)
    }
  },

  /** Per-variant so the patch is type-checked against the block it addresses; a block of a different
   *  type at `index` is a no-op rather than a corrupted union member. */
  updateTextBlock (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    index: number,
    patch: TextBlockPatch,
  ): void {
    const block = findContent(this, owner)?.[index]
    if (block?.type !== 'text') {
      return
    }
    patchRequired(block, 'value', patch.value)
    patchOptional(block, 'audioUrl', patch.audioUrl)
  },

  updateImageBlock (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    index: number,
    patch: ImageBlockPatch,
  ): void {
    const block = findContent(this, owner)?.[index]
    if (block?.type !== 'image') {
      return
    }
    if (patch.url !== undefined) {
      block.asset.url = patch.url
    }
    patchRequired(block, 'alt', patch.alt)
  },

  updateAudioBlock (
    this: AssessmentEditorStore,
    owner: ContentBlockOwner,
    index: number,
    patch: AudioBlockPatch,
  ): void {
    const block = findContent(this, owner)?.[index]
    if (block?.type !== 'audio') {
      return
    }
    if (patch.url !== undefined) {
      block.asset.url = patch.url
    }
  },

  // ---- activities ----------------------------------------------------------

  /** Appends an empty activity to the screen, selects it, and returns its id. */
  addActivity (this: AssessmentEditorStore, screenId: string): string | null {
    const screen = findScreen(this, screenId)
    if (!screen) {
      return null
    }
    const activity = createActivity()
    screen.activities.push(activity)
    this.selectedScreenId = screenId
    this.selectedActivityId = activity.id
    return activity.id
  },

  removeActivity (this: AssessmentEditorStore, screenId: string, activityId: string): void {
    const screen = findScreen(this, screenId)
    if (!screen) {
      return
    }
    const index = screen.activities.findIndex(activity => activity.id === activityId)
    if (index < 0) {
      return
    }
    const removed = screen.activities.splice(index, 1)
    Reflect.deleteProperty(this.interactionDrafts, activityId)
    dropRubricsOf(this, removed)
    if (this.selectedActivityId === activityId) {
      this.selectedActivityId = neighbourId(screen.activities, index)
    }
  },

  moveActivityUp (this: AssessmentEditorStore, screenId: string, activityId: string): void {
    const activities = findScreen(this, screenId)?.activities
    if (activities) {
      moveWithin(activities, activities.findIndex(activity => activity.id === activityId), -1)
    }
  },

  moveActivityDown (this: AssessmentEditorStore, screenId: string, activityId: string): void {
    const activities = findScreen(this, screenId)?.activities
    if (activities) {
      moveWithin(activities, activities.findIndex(activity => activity.id === activityId), 1)
    }
  },

  updateActivity (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: ActivityPatch,
  ): void {
    const activity = findActivity(this, screenId, activityId)
    if (!activity) {
      return
    }
    patchOptional(activity, 'name', patch.name)
    patchOptional(activity, 'points', patch.points)
  },

  /**
   * The per-activity standards PICKER — the whole `Activity.standards` array at once, the same
   * shape as `setActivityRubrics`. An empty array deletes the key rather than storing `[]`, which
   * is what the schema's `.min(1)` expects.
   *
   * Writes the array as given, and that is the point: a code the picker would no longer offer is
   * not filtered out here, because dropping it would silently edit a document the author never
   * touched (B12.5.25). The chip stays, removable but not re-addable while filtered out.
   *
   * CODES, not `{ code, label }` objects. A stored legacy entry is left alone until the author edits
   * this field; then the whole array is rewritten as codes — the one rewrite B9 permits, because it
   * is the field being edited.
   */
  setActivityStandards (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    codes: string[],
  ): void {
    const activity = findActivity(this, screenId, activityId)
    if (!activity) {
      return
    }
    patchOptional(activity, 'standards', codes.length > 0 ? codes : null)
  },

  /**
   * The per-RUBRIC standards picker — `ReviewRubric.standards`, codes only and with no legacy shape
   * to accept. Separate from `setActivityStandards` because they address different nodes, not
   * because they behave differently. Empty deletes the key, as everywhere else.
   */
  setRubricStandards (
    this: AssessmentEditorStore,
    rubricId: string,
    codes: string[],
  ): void {
    const rubric = findRubric(this, rubricId)
    if (!rubric) {
      return
    }
    patchOptional(rubric, 'standards', codes.length > 0 ? codes : null)
  },

  /**
   * The per-activity rubric PICKER — an array of catalog IDS (`Activity.rubrics`), never the catalog
   * itself (B8). A multi-select, so it sets the whole array; an empty array deletes the key.
   */
  setActivityRubrics (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    rubricIds: string[],
  ): void {
    const activity = findActivity(this, screenId, activityId)
    if (!activity) {
      return
    }
    patchOptional(activity, 'rubrics', rubricIds.length > 0 ? rubricIds : null)
  },

  /**
   * Switches (or clears, with `null`) an activity's interaction type.
   *
   * The outgoing interaction is STASHED, NOT DROPPED: discarding a half-authored multiple choice
   * because the user peeked at another type is hostile, and keeping its fields beside the new ones
   * would break the discriminated union. So it is deep-copied into `interactionDrafts[activityId]`
   * — session state outside the document, which therefore never reaches the API — and switching
   * back restores a copy of it.
   */
  setInteractionType (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    type: InteractionType | null,
  ): void {
    const activity = findActivity(this, screenId, activityId)
    if (!activity) {
      return
    }
    const current = activity.interaction
    if (current) {
      this.interactionDrafts[activityId] = {
        ...this.interactionDrafts[activityId],
        [current.type]: cloneJson(current),
      }
    }
    if (type === null) {
      delete activity.interaction
      return
    }
    if (current?.type === type) {
      return
    }
    const stashed = this.interactionDrafts[activityId]?.[type]
    activity.interaction = stashed ? cloneJson(stashed) : createInteraction(type)
  },

  // ---- multiple_choice: mode + options -------------------------------------

  /**
   * `multi` → `single` keeps the FIRST correct option and clears the rest. The other direction needs
   * nothing: several correct options are legal under `multi`. This way round, refinement 1 rejects
   * more than one correct option, and leaving the extra ticks would author a document that is
   * invalid the moment the mode changed, on options the mode control does not show. It is the
   * missing half of the rule `updateChoice` already applies. Zero correct options stay zero:
   * inventing an answer key would be worse than the validation error.
   */
  updateMultipleChoice (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: MultipleChoicePatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'multiple_choice')
    if (!interaction) {
      return
    }
    const previousMode = interaction.mode
    patchRequired(interaction, 'mode', patch.mode)
    if (interaction.mode !== 'single' || previousMode === 'single') {
      return
    }
    let kept = false
    interaction.options.forEach((option) => {
      if (!option.correct) {
        return
      }
      if (!kept) {
        kept = true
        return
      }
      option.correct = false
    })
  },

  addChoice (this: AssessmentEditorStore, screenId: string, activityId: string): string | null {
    const interaction = findInteraction(this, screenId, activityId, 'multiple_choice')
    if (!interaction) {
      return null
    }
    const choice = createChoice()
    interaction.options.push(choice)
    return choice.id
  },

  removeChoice (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    choiceId: string,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'multiple_choice')
    if (!interaction) {
      return
    }
    const index = interaction.options.findIndex(option => option.id === choiceId)
    if (index < 0) {
      return
    }
    interaction.options.splice(index, 1)
  },

  moveChoiceUp (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    choiceId: string,
  ): void {
    const options = findInteraction(this, screenId, activityId, 'multiple_choice')?.options
    if (options) {
      moveWithin(options, options.findIndex(option => option.id === choiceId), -1)
    }
  },

  moveChoiceDown (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    choiceId: string,
  ): void {
    const options = findInteraction(this, screenId, activityId, 'multiple_choice')?.options
    if (options) {
      moveWithin(options, options.findIndex(option => option.id === choiceId), 1)
    }
  },

  /** In `single` mode, marking an option correct clears the others — radio semantics, and the only
   *  way the form can satisfy refinement 1 without the author hunting for the old answer. */
  updateChoice (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    choiceId: string,
    patch: ChoicePatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'multiple_choice')
    const choice = interaction?.options.find(option => option.id === choiceId)
    if (!interaction || !choice) {
      return
    }
    patchRequired(choice, 'text', patch.text)
    if (patch.correct === undefined) {
      return
    }
    choice.correct = patch.correct
    if (patch.correct && interaction.mode === 'single') {
      interaction.options.forEach((option) => {
        if (option.id !== choiceId) {
          option.correct = false
        }
      })
    }
  },

  // ---- fill_in_blanks: text + blanks ---------------------------------------

  updateFillInBlanks (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: FillInBlanksPatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'fill_in_blanks')
    if (!interaction) {
      return
    }
    patchRequired(interaction, 'text', patch.text)
    patchRequired(interaction, 'blanks', patch.blanks)
  },

  // No per-blank actions. `blanks` is derived from the authored text by `lib/assignments/fill-in-blanks.ts`
  // and written wholesale in the patch above — there is no answer key to add, remove or reorder
  // independently of the sentence, which is what retired the positional "which gap is this?"
  // problem.

  // ---- drag_the_words / whiteboard -----------------------------------------

  updateDragTheWords (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: DragTheWordsPatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'drag_the_words')
    if (!interaction) {
      return
    }
    patchRequired(interaction, 'text', patch.text)
    patchOptional(
      interaction,
      'distractors',
      patch.distractors && patch.distractors.length === 0 ? null : patch.distractors,
    )
  },

  updateWhiteboard (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: WhiteboardPatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'whiteboard')
    if (!interaction) {
      return
    }
    // `null` clears the whole node; a URL is written INTO the existing one rather than rebuilt from
    // `{ url }`, so anything the node carries beyond `url` survives (invariant 1 — `PATCH` replaces
    // the row). Mirrors `updateImageBlock`.
    if (patch.backgroundUrl === null) {
      patchOptional(interaction, 'backgroundAsset', null)
    } else if (patch.backgroundUrl !== undefined) {
      if (interaction.backgroundAsset) {
        interaction.backgroundAsset.url = patch.backgroundUrl
      } else {
        interaction.backgroundAsset = { url: patch.backgroundUrl }
      }
    }
    patchOptional(interaction, 'backgroundAlt', patch.backgroundAlt)
  },

  // ---- open_ended ----------------------------------------------------------

  /** An emptied list is written as `[]`, never deleted: absent means text only, so deleting the key
   *  would turn "nothing ticked" into a valid text-only activity instead of the save rule's error. */
  updateOpenEnded (
    this: AssessmentEditorStore,
    screenId: string,
    activityId: string,
    patch: OpenEndedPatch,
  ): void {
    const interaction = findInteraction(this, screenId, activityId, 'open_ended')
    if (!interaction) {
      return
    }
    patchRequired(interaction, 'formats', patch.formats && [...patch.formats])
  },

  // --- rubric catalog -------------------------------------------------------
  //
  // `data.rubrics` is a MAP (B8), not a list, so add/remove are keyed insert/delete and there is
  // deliberately no `moveRubricUp/Down`. Nor one for `levels`: the bands are fixed
  // (`RUBRIC_LEVEL_SCORES`), so a rubric's one array has no orderable UI either.

  /** Inserts an empty rubric and returns its id (`rubric-…`; never numeric, because these ids are
   *  map keys — B7). No `kind` parameter: there is one rubric type (B10). */
  addRubric (this: AssessmentEditorStore): string | null {
    const catalog = ensureCatalog(this)
    if (!catalog) {
      return null
    }
    const rubric = createRubric()
    catalog[rubric.id] = rubric
    return rubric.id
  },

  /** Also drops the id from every `activity.rubrics`, so removing a rubric cannot leave a dangling
   *  reference that fails refinement 5 on a screen the author never opened. */
  removeRubric (this: AssessmentEditorStore, rubricId: string): void {
    if (!this.workingDocument?.rubrics?.[rubricId]) {
      return
    }
    Reflect.deleteProperty(this.workingDocument.rubrics, rubricId)
    screensOf(this).forEach((screen) => {
      screen.activities.forEach((activity) => {
        if (!activity.rubrics?.includes(rubricId)) {
          return
        }
        const rest = activity.rubrics.filter(id => id !== rubricId)
        // An emptied list DELETES the key, exactly as `setActivityRubrics` does: `rubrics: []` and
        // no `rubrics` at all mean the same thing.
        patchOptional(activity, 'rubrics', rest.length > 0 ? rest : null)
      })
    })
  },

  /** Every scalar field of a rubric — one type, one update action (decision B10). */
  updateRubric (this: AssessmentEditorStore, rubricId: string, patch: RubricPatch): void {
    const rubric = findRubric(this, rubricId)
    if (!rubric) {
      return
    }
    patchRequired(rubric, 'name', patch.name)
    patchOptional(rubric, 'instructions', patch.instructions)
    // No `standards` — a rubric no longer carries them (B12.1.2). A pre-B12 rubric that still holds
    // the key keeps it: nothing here strips it, so the document round-trips unchanged (B9).
  },

  // --- rubric levels — three fixed bands, addressed by SCORE -----------------
  //
  // The levels list is static (`RUBRIC_LEVEL_SCORES`): no add, no remove, no reorder and no score
  // setter, so this one action is the whole sub-form. Addressing by score rather than by index is
  // what makes that safe — an index would retarget if a stored rubric ordered its bands otherwise.

  /**
   * Patches the band scored `score`, inserting it IN SCORE ORDER when the rubric does not carry that
   * band yet. Not a repair pass (B9): it fires only for the band the author is typing into, so a
   * legacy rubric gains a band 0 exactly when someone writes one, keeping everything else untouched.
   */
  setRubricLevel (
    this: AssessmentEditorStore,
    rubricId: string,
    score: number,
    patch: RubricLevelPatch,
  ): void {
    const rubric = findRubric(this, rubricId)
    if (!rubric || !hasLevels(rubric)) {
      return
    }

    let level = rubric.levels.find(item => item.score === score)

    if (!level) {
      level = { score } satisfies ScoredRubricLevel
      const at = rubric.levels.findIndex(item => item.score > score)
      rubric.levels.splice(at === -1 ? rubric.levels.length : at, 0, level)
    }

    patchOptional(level, 'description', patch.description)
  },
}
